import { Classes, Label } from '@blueprintjs/core';
import React, {
  forwardRef,
  Fragment,
  ReactNode,
  useImperativeHandle
} from 'react';

type Props = {
  children: ReactNode;
  handleExportOptionsChange?: (option: ExportOption) => void;
  exportOption: ExportOption;
};

export interface ExportOption {
  supportedFileFormats: string[];
  fileFormat: 'json' | 'csv' | 'xml' | 'pdf' | 'txt';
  filename: string;
  isExporting: boolean;
}

export type ExportReportDialogBodyFormRef = {
  getExportOption: () => ExportOption;
};

interface Option {
  label: string;
  value: string;
}

const ExportReportDialogBody = forwardRef<ExportReportDialogBodyFormRef, Props>(
  ({ children, exportOption, handleExportOptionsChange }: Props, ref) => {
    const [selectedFileFormat, setSelectedFileFormat] = React.useState<Option>({
      label: exportOption.fileFormat,
      value: exportOption.fileFormat
    });

    const [filename, setFilename] = React.useState<string>(
      exportOption.filename
    );

    useImperativeHandle(ref, () => {
      return {
        getExportOption: () => {
          return {
            supportedFileFormats: exportOption.supportedFileFormats,
            fileFormat: selectedFileFormat.value as
              | 'json'
              | 'csv'
              | 'xml'
              | 'pdf'
              | 'txt',
            filename,
            isExporting: exportOption.isExporting
          };
        }
      };
    });

    const handleFileFormatSelect = (
      event: React.ChangeEvent<HTMLSelectElement>
    ) => {
      const newFormat = event.target.value as
        | 'json'
        | 'csv'
        | 'xml'
        | 'pdf'
        | 'txt';

      setSelectedFileFormat({
        label: newFormat.toLocaleUpperCase(),
        value: newFormat.toLocaleLowerCase()
      });
      if (handleExportOptionsChange) {
        handleExportOptionsChange({
          ...exportOption,
          fileFormat: newFormat
        });
      }
    };

    const renderOptions = () => {
      return exportOption.supportedFileFormats.map((format) => (
        <option key={format} value={format.toLocaleLowerCase()}>
          {format.toLocaleUpperCase()}
        </option>
      ));
    };

    const handleFilenameChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setFilename(event.target.value);
      if (handleExportOptionsChange) {
        handleExportOptionsChange({
          ...exportOption,
          filename: event.target.value
        });
      }
    };

    return (
      <Fragment>
        {children}
        <br />
        <br />
        <Label>
          File format:
          <div className="bp3-select">
            <select
              value={selectedFileFormat.value}
              onChange={handleFileFormatSelect}
            >
              {renderOptions()}
            </select>
          </div>
        </Label>

        <div style={{ marginBottom: '8px' }} />

        <Label style={{ marginTop: '4px' }}>
          Filename:
          <input
            style={{ width: '100%' }}
            className={Classes.INPUT}
            value={filename}
            onChange={handleFilenameChange}
          />
        </Label>
      </Fragment>
    );
  }
);

export default ExportReportDialogBody;
